import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ScanLine } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { label: "Court Scan", to: "/court-scan" },
  { label: "Customiser", to: "/customiser" },
  { label: "Services", to: "/services" },
  { label: "The Method", to: "/method" },
  { label: "Blog Articles", to: "/blog-articles" },
  { label: "The Archive", to: "/gallery" },
  { label: "Team Speedy", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const isActive = (to) => loc.pathname === to || loc.pathname.startsWith(`${to}/`);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      {!scrolled && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-obsidian/80 via-obsidian/30 to-transparent" />
      )}
      <nav className="relative mx-auto flex max-w-7xl items-center justify-between px-5 md:px-10">
        <Logo />

        <div className="hidden xl:flex items-center gap-5 xl:gap-8">
          {LINKS.map((l) => {
            const active = isActive(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                aria-current={active ? "page" : undefined}
                data-testid={`nav-${l.to.replace(/\//g, "")}`}
                data-active={active || undefined}
                className={`nav-link text-sm font-medium transition-colors ${active ? "nav-link-active text-gold" : "text-zinc-100 hover:text-neon"}`}
              >
                {l.label}
              </Link>
            );
          })}
        </div>

        <div className="hidden xl:flex items-center gap-3">
          <button
            onClick={() => nav("/quote")}
            data-testid="nav-quote-btn"
            className="rounded-sm bg-neon px-5 py-2.5 text-sm font-bold text-obsidian hover:bg-neon-hover transition-colors shadow-neon"
          >
            Request a Quote
          </button>
        </div>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          className="xl:hidden text-white p-2"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="xl:hidden glass mt-3 mx-4 rounded-sm p-6 flex flex-col gap-4 max-h-[calc(100vh-110px)] overflow-y-auto" data-testid="mobile-menu">
          {LINKS.map((l) => {
            const active = isActive(l.to);
            return (
              <Link key={l.to} to={l.to} aria-current={active ? "page" : undefined} data-testid={`mobile-nav-${l.to.replace(/\//g, "")}`}
                    className={`text-lg font-heading font-bold ${active ? "nav-link-active text-gold" : "text-white hover:text-neon"}`}>
                {l.label}
              </Link>
            );
          })}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-white/10 pt-4">
            <Link to="/assessment" className="text-sm text-zinc-300 hover:text-neon">Free Site Assessment</Link>
            <Link to="/compliance" className="text-sm text-zinc-300 hover:text-neon">Cm3 Compliance</Link>
            <a href="tel:0466495495" className="text-sm text-gold">0466 495 495</a>
            <a href="mailto:info@speedyresurfacing.com.au" className="text-sm text-zinc-300 truncate">info@speedyresurfacing.com.au</a>
          </div>
          <div className="flex gap-3 pt-2">
            <Link to="/court-scan" className="flex-1 flex items-center justify-center gap-2 rounded-sm border border-neon/40 px-4 py-3 text-sm font-bold text-neon">
              <ScanLine size={16} /> Court Scan
            </Link>
            <Link to="/quote" className="flex-1 rounded-sm bg-neon px-4 py-3 text-center text-sm font-bold text-obsidian">
              Get a Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
